/**
 * Accounting checks: what Timely noticed in TallyPrime's figures that affects
 * how clearly they can be collected on (docs/tally/19).
 *
 * The point is not to mark the accountant's work. Most of what is here is
 * ordinary accounting — money On Account, an advance not yet adjusted, a
 * ledger kept without bills — and Timely works with all of it. Each check
 * says, in plain words and in this order:
 *
 *   1. what Timely found;
 *   2. what it means;
 *   3. how Timely handles it;
 *   4. whether anyone needs to do anything;
 *   5. whether syncing can continue.
 *
 * Four levels, and the last is rare on purpose:
 *
 *   information         expected, or harmless, but worth knowing;
 *   review              Timely carries on safely; worth a look;
 *   attention           an inconsistency that affects the figures' clarity;
 *   blocks              Timely cannot tell what it would connect.
 *
 * A check is a fact about the latest read. When TallyPrime changes, the next
 * read re-runs every check, so a fixed condition resolves by itself.
 * Acknowledging a check (migration 0036) is a note in Timely: it changes
 * nothing in TallyPrime and nothing in the figures.
 *
 * On screen this is "Accounting checks", never "health": PRODUCT-UX-
 * FOUNDATION §14 keeps judgement words off figures that are facts.
 */
import type { CreditEvent, ReconcileResult } from './allocationConfidence.js';
import { EXPLANATIONS } from './allocationConfidence.js';
import type { ChequeForMatching, ChequeMatch } from './chequeMatching.js';
import type { OperationalAllocation } from './operationalAllocation.js';
import { addDays, daysFrom, fromPaise, toPaise, type SourceFacts } from './receivablesFacts.js';
import type { ReceivablesPosition } from './receivablesPosition.js';
import type { ReceivablesSettings } from './receivablesSettings.js';

export type CheckSeverity = 'information' | 'review' | 'attention' | 'blocks';

export const SEVERITY_ORDER: readonly CheckSeverity[] = ['blocks', 'attention', 'review', 'information'];

export const SEVERITY_WORDS: Record<CheckSeverity, string> = {
    information: 'Information',
    review: 'Review recommended',
    attention: 'Needs attention',
    blocks: 'Blocks connecting',
};

export type CheckCode =
    | 'on_account_credit' | 'on_account_debit' | 'credit_reference_open' | 'account_in_credit' | 'not_bill_wise'
    | 'due_on_bill_date' | 'no_due_date' | 'old_open_bill' | 'duplicate_bill_reference' | 'bills_not_matched'
    | 'future_dated_bill' | 'balance_nil_bills_open' | 'overdue_days_differ' | 'no_customer_ledgers' | 'nothing_aged'
    | 'excluded_by_settings' | 'tally_working_date'
    | 'allocation_unclear' | 'receipt_partly_allocated' | 'receipt_across_bills' | 'bill_pending_again'
    | 'voucher_changed' | 'movement_not_explained' | 'company_changed' | 'ledger_no_longer_read'
    | 'cheque_not_in_tally' | 'cheque_bounced_receipt_stands' | 'receipt_without_cheque'
    | 'timely_allocation_needs_review';

export interface CheckDefinition {
    code: CheckCode;
    severity: CheckSeverity;
    title: string;
    means: string;
    handling: string;
    action: string;
    sync: string;
}

const SAFE = 'Safe to continue. Once TallyPrime changes, Timely updates by itself.';

/** Every check, defined once. The screens, the docs and the tests read this list. */
export const CHECKS: readonly CheckDefinition[] = [
    {
        code: 'no_customer_ledgers', severity: 'blocks', title: 'No customer ledgers found',
        means: 'TallyPrime returned no ledgers under Sundry Debtors for this company. Either it keeps no customers in TallyPrime, or a different company holds them.',
        handling: 'Nothing can be connected from this read.',
        action: 'Check that the company chosen is the one with your customers, and that it is open in TallyPrime.',
        sync: 'Cannot connect until TallyPrime shows customer ledgers.',
    },
    {
        code: 'company_changed', severity: 'blocks', title: 'A different TallyPrime company',
        means: 'This read is of a different TallyPrime company from the one connected.',
        handling: 'Nothing from it is used. The figures stay as they were.',
        action: 'Open the connected company in TallyPrime, or connect the other company on purpose.',
        sync: 'Stopped for this company until the connected company is read again.',
    },
    {
        code: 'bills_not_matched', severity: 'attention', title: 'Bills not tied to one customer',
        means: 'Some bills could not be tied to exactly one customer ledger — usually two ledgers with the same name in different groups.',
        handling: 'Those bills are left out of the ageing; the ledgers’ balances are still counted in full. Nothing is guessed.',
        action: 'Your accountant may give one of the ledgers a different name in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'duplicate_bill_reference', severity: 'attention', title: 'A reference used twice',
        means: 'Two open bills on one account share a reference, so a receipt entered against that reference could belong to either.',
        handling: 'Both bills are shown and aged by their own dates. Timely does not merge them, and does not work out allocations for them.',
        action: 'Your accountant may give each bill its own reference in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'overdue_days_differ', severity: 'attention', title: 'Overdue days read differently',
        means: 'TallyPrime counts every bill’s overdue days to one date, its working date. For these bills its overdue days point to a different date from the others — usually a date read differently.',
        handling: 'Timely ages by the dates TallyPrime reports.',
        action: 'Let Timely support know, so the reading can be checked.',
        sync: SAFE,
    },
    {
        code: 'tally_working_date', severity: 'review', title: 'TallyPrime is set to another date',
        means: 'TallyPrime works to the date set in it (F2), not to today’s: it counts its overdue days to that date, and its bills reports may leave out anything dated after it.',
        handling: 'Timely ages every bill to today, from the bill’s own dates. TallyPrime’s overdue days are not used.',
        action: 'If this is not on purpose, set TallyPrime’s date to today (F2). Timely updates by itself at the next update.',
        sync: SAFE,
    },
    {
        code: 'nothing_aged', severity: 'attention', title: 'Nothing can be aged',
        means: 'No customer ledger keeps its balance bill by bill, so TallyPrime has no bills or dates for Timely to age.',
        handling: 'Balances are shown in full and as not aged. Timely does not invent bills or dates.',
        action: 'If you want ageing, your accountant can turn on “Maintain balances bill-by-bill” for customer ledgers in TallyPrime and enter their open bills.',
        sync: SAFE,
    },
    {
        code: 'bill_pending_again', severity: 'attention', title: 'A bill is pending again',
        means: 'A bill that had fallen or closed is pending again in TallyPrime. A receipt may have been cancelled or reversed (a returned cheque, for example), or the bill was increased.',
        handling: 'Timely shows what TallyPrime shows now. The earlier change stays in the history; nothing is marked paid that TallyPrime says is not.',
        action: 'None if it was expected. Otherwise, worth checking with your accountant.',
        sync: SAFE,
    },
    {
        code: 'cheque_bounced_receipt_stands', severity: 'attention', title: 'Bounced in Timely, received in TallyPrime',
        means: 'A cheque is marked bounced in Timely, but TallyPrime still records the receipt it was linked to.',
        handling: 'What the customer owes stays as TallyPrime states it.',
        action: 'Check whether the cheque really came back. If it did, your accountant can reverse the receipt in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'on_account_credit', severity: 'review', title: 'Received On Account',
        means: 'Money was entered in TallyPrime against no particular bill, so the account’s balance is lower than its open bills.',
        handling: 'Timely shows it as unallocated credit and lowers the net by it. It is not put against any bill, so the bills keep their ageing. Timely will not guess.',
        action: 'If it is known which bills it was for, your accountant may allocate it in TallyPrime. Until then, someone in Timely may record a Timely allocation for collections.',
        sync: SAFE,
    },
    {
        code: 'not_bill_wise', severity: 'review', title: 'Kept without bills',
        means: 'This ledger does not maintain its balance bill by bill in TallyPrime, so there are no bills or dates to age.',
        handling: 'The balance is included in full and shown as not aged. Timely does not invent bills or dates for it.',
        action: 'Optional: your accountant can turn on “Maintain balances bill-by-bill” for the ledger in TallyPrime and enter its open bills.',
        sync: SAFE,
    },
    {
        code: 'on_account_debit', severity: 'review', title: 'Owed with no bill',
        means: 'Part of the balance is in no bill — often an opening balance entered without bill details, or a debit entered On Account.',
        handling: 'It is included in what is owed and shown as not aged. Timely does not put it in a bucket.',
        action: 'Optional: your accountant can enter its bill details in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'old_open_bill', severity: 'review', title: 'Bills over a year old',
        means: 'Bills this old are often disputed, or were paid but never allocated in TallyPrime.',
        handling: 'They are aged like any other bill, in the oldest bucket.',
        action: 'Worth confirming with your accountant that they are still owed.',
        sync: SAFE,
    },
    {
        code: 'balance_nil_bills_open', severity: 'review', title: 'Nil balance, bills still open',
        means: 'A bill and a credit (an advance or a credit note) cancel out in the balance, but are not set against each other in TallyPrime.',
        handling: 'The account owes nothing and is not chased. Its open bills and credit are listed.',
        action: 'Your accountant may adjust the credit against the bill in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'allocation_unclear', severity: 'review', title: 'Allocation needs review',
        means: 'TallyPrime shows this receivable changed, but Timely cannot reliably determine which bill(s) were settled.',
        handling: 'The change is shown as TallyPrime’s bills show it. No bill is marked paid on a guess.',
        action: 'None in TallyPrime. If the customer said which bills it was for, someone in Timely may record a Timely allocation for collections.',
        sync: SAFE,
    },
    {
        code: 'movement_not_explained', severity: 'review', title: 'Bills moved without a voucher read',
        means: 'TallyPrime’s bills changed by more than the receipts, credit notes and journals Timely read explain — for example, an invoice deleted or changed in TallyPrime, or a voucher dated outside the period read.',
        handling: 'The bills are shown as TallyPrime states them. The change is not counted as money received, and no allocation is worked out from it.',
        action: 'None, if an invoice was changed or deleted on purpose.',
        sync: SAFE,
    },
    {
        code: 'ledger_no_longer_read', severity: 'review', title: 'A customer ledger is no longer read',
        means: 'A ledger that was among the customer ledgers is no longer under Sundry Debtors in TallyPrime — it was moved to another group, renamed into one, or deleted.',
        handling: 'As with any customer TallyPrime no longer lists, its balance in Timely settles. Its notes, promises and history stay.',
        action: 'If it should still be chased, your accountant can move it back under Sundry Debtors in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'cheque_not_in_tally', severity: 'review', title: 'Cheque not yet in TallyPrime',
        means: 'A cheque recorded in Timely has no receipt in TallyPrime yet.',
        handling: 'What the customer owes stays as TallyPrime states it. The cheque is tracked in Timely as an instrument and is not counted as money received.',
        action: 'If it has been banked, your accountant may enter the receipt in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'timely_allocation_needs_review', severity: 'review', title: 'A Timely allocation needs review',
        means: 'TallyPrime changed in a way that no longer fits a Timely allocation for collections.',
        handling: 'The allocation is set aside (not used) and kept for the record. TallyPrime’s figures are used.',
        action: 'Someone in Timely can review it and record a new one if it is still needed.',
        sync: SAFE,
    },
    {
        code: 'credit_reference_open', severity: 'information', title: 'Credit held for the customer',
        means: 'A credit note entered as a new reference, or an advance, is held for the customer and not yet set against a bill.',
        handling: 'Shown apart as credit and taken off the net. It is never put into an ageing bucket by its own date.',
        action: 'None. When it is adjusted against a bill in TallyPrime, Timely updates by itself.',
        sync: SAFE,
    },
    {
        code: 'account_in_credit', severity: 'information', title: 'Account in credit',
        means: 'TallyPrime holds money for this customer — an advance, an overpayment or a credit note.',
        handling: 'Shown under “Held for customers”; it reduces the net; it is never chased as owing.',
        action: 'None.',
        sync: SAFE,
    },
    {
        code: 'due_on_bill_date', severity: 'information', title: 'Due on the bill date',
        means: 'These bills have no credit period in TallyPrime, so TallyPrime gives them their bill date as the due date.',
        handling: 'Ageing by due date treats them as TallyPrime does.',
        action: 'None, unless these customers have credit terms — then your accountant may set a credit period on the ledger in TallyPrime.',
        sync: SAFE,
    },
    {
        code: 'no_due_date', severity: 'information', title: 'No due date from TallyPrime',
        means: 'TallyPrime gave no due date for these bills.',
        handling: 'By your settings, due-date ageing either treats them as due on the bill date or leaves them out of the buckets as not aged. Their dates are never invented.',
        action: 'None.',
        sync: SAFE,
    },
    {
        code: 'future_dated_bill', severity: 'information', title: 'Dated in the future',
        means: 'Some bills are dated after the date the figures are stated at.',
        handling: 'They are aged as current (0 days) until their date.',
        action: 'None, if they were meant to be dated ahead.',
        sync: SAFE,
    },
    {
        code: 'excluded_by_settings', severity: 'information', title: 'Left out by your settings',
        means: 'Your receivables settings leave these accounts out.',
        handling: 'They are read, listed here and counted apart; they are not in the totals.',
        action: 'None. Change the settings to bring them back.',
        sync: SAFE,
    },
    {
        code: 'receipt_partly_allocated', severity: 'information', title: 'Receipt partly allocated',
        means: 'Part of a receipt is entered against bills in TallyPrime; the rest is against no bill.',
        handling: 'The allocated part is shown against its bills; the rest as unallocated credit.',
        action: 'None.',
        sync: SAFE,
    },
    {
        code: 'receipt_across_bills', severity: 'information', title: 'One receipt, several bills',
        means: 'A receipt was entered in TallyPrime against more than one bill.',
        handling: 'Each bill shows its share, as TallyPrime states it.',
        action: 'None.',
        sync: SAFE,
    },
    {
        code: 'voucher_changed', severity: 'information', title: 'Changed in TallyPrime',
        means: 'A voucher Timely had already read was altered in TallyPrime.',
        handling: 'Timely uses the voucher as it is now and keeps a record that it changed. Notes, promises and history are untouched.',
        action: 'None.',
        sync: SAFE,
    },
    {
        code: 'receipt_without_cheque', severity: 'information', title: 'Receipt with a cheque number, no cheque in Timely',
        means: 'TallyPrime records a cheque receipt that no cheque in Timely is linked to.',
        handling: 'The receipt counts as money received, from TallyPrime. Nothing else changes.',
        action: 'None. If a cheque was recorded in Timely under a different number or amount, it can be linked by hand.',
        sync: SAFE,
    },
];

export const checkDefinition = (code: CheckCode): CheckDefinition => CHECKS.find(c => c.code === code)!;

export interface Finding {
    /** Stable across reads while the condition lasts: code and subject. */
    fingerprint: string;
    code: CheckCode;
    severity: CheckSeverity;
    sourceKey?: string;
    account?: string;
    /** Amounts in rupees, counts, references, dates: rendered by `describeFinding` in the company's own formats. */
    facts: { amount?: number; count?: number; refs?: string[]; date?: string; detail?: string };
}

export interface ChecksInput {
    facts: Pick<SourceFacts, 'asOf' | 'accounts' | 'unmatchedBills' | 'source'>;
    position: ReceivablesPosition;
    settings: ReceivablesSettings;
    /** The previous read (its company, and its accounts), when there was one. */
    previous?: Pick<SourceFacts, 'source' | 'accounts'> | null;
    reconcile?: ReconcileResult | null;
    cheques?: { cheques: readonly ChequeForMatching[]; matches: readonly ChequeMatch[]; recordsCheques: boolean } | null;
    operational?: readonly OperationalAllocation[];
}

/** A bill this many days old or older is "over a year old". */
export const OLD_BILL_DAYS = 365;
/** A cheque cleared in Timely this many days ago with no receipt in TallyPrime is worth a look. */
export const CHEQUE_NOT_IN_TALLY_DAYS = 7;

/**
 * The date TallyPrime counted its overdue days to — its working date — from
 * the bills it sent: the date most bills point to, and the bills that point
 * elsewhere. A bill TallyPrime calls not overdue must be due after that date.
 */
export function tallyWorkingDate(
    countedTo: readonly { ref: string; date: string }[],
    notYetDue: readonly { ref: string; due: string }[] = [],
): { date: string | null; disagree: string[] } {
    if (countedTo.length === 0) return { date: null, disagree: [] };
    const votes = new Map<string, number>();
    for (const c of countedTo) votes.set(c.date, (votes.get(c.date) ?? 0) + 1);
    const date = [...votes.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? 1 : -1))[0]![0];
    const disagree = [
        ...countedTo.filter(c => c.date !== date).map(c => c.ref),
        ...notYetDue.filter(b => b.due <= date).map(b => b.ref),
    ];
    return { date, disagree };
}

export function runSourceChecks(input: ChecksInput): Finding[] {
    const out: Finding[] = [];
    const push = (code: CheckCode, subject: string, facts: Finding['facts'], account?: { sourceKey: string; name: string }) => {
        out.push({
            fingerprint: `${code}:${subject}`, code, severity: checkDefinition(code).severity,
            ...(account ? { sourceKey: account.sourceKey, account: account.name } : {}), facts,
        });
    };
    const { facts, position, settings } = input;

    if (input.previous && input.previous.source.companyId.toLowerCase() !== facts.source.companyId.toLowerCase()) {
        push('company_changed', 'company', { detail: facts.source.companyName });
        return out;
    }
    if (facts.accounts.length === 0) { push('no_customer_ledgers', 'company', {}); return out; }

    const owedP = toPaise(position.summary.owedToYou);
    if (owedP > 0 && toPaise(position.summary.ageing.aged.amount) === 0) {
        push('nothing_aged', 'company', { amount: position.summary.owedToYou });
    }
    if (facts.unmatchedBills.length) {
        push('bills_not_matched', 'company', { count: facts.unmatchedBills.length, amount: fromPaise(facts.unmatchedBills.reduce((s, b) => s + toPaise(b.pending), 0)), refs: facts.unmatchedBills.slice(0, 10).map(b => b.ref) });
    }

    let dueOnBillDate = 0, dueOnBillDateP = 0, noDue = 0, noDueP = 0, future = 0, futureP = 0;
    // TallyPrime counts BILLOVERDUE to its own working date (the F2 date), whatever date the request asks for (proved on
    // TallyPrime 7.1, 5 Oct 2026). So each bill's due date plus its overdue days names that date; the bills must agree.
    const countedTo: { ref: string; date: string }[] = [];
    const notYetDue: { ref: string; due: string }[] = [];
    for (const account of facts.accounts) {
        const pos = position.accounts.find(p => p.sourceKey === account.sourceKey);
        const who = { sourceKey: account.sourceKey, name: account.name };
        if (pos && !pos.inScope) continue;
        const closingP = toPaise(account.closing);
        const unbilledP = toPaise(account.unbilled);
        if (!account.billWise) {
            if (closingP !== 0) push('not_bill_wise', account.sourceKey, { amount: account.closing }, who);
        } else {
            if (unbilledP < 0) push('on_account_credit', account.sourceKey, { amount: fromPaise(-unbilledP) }, who);
            if (unbilledP > 0) push('on_account_debit', account.sourceKey, { amount: fromPaise(unbilledP) }, who);
        }
        if (closingP < 0) push('account_in_credit', account.sourceKey, { amount: fromPaise(-closingP) }, who);
        const credits = account.bills.filter(b => toPaise(b.pending) < 0);
        if (credits.length) push('credit_reference_open', account.sourceKey, { amount: fromPaise(-credits.reduce((s, b) => s + toPaise(b.pending), 0)), count: credits.length, refs: credits.map(b => b.ref) }, who);
        if (closingP === 0 && account.bills.length) push('balance_nil_bills_open', account.sourceKey, { count: account.bills.length, amount: fromPaise(account.bills.filter(b => toPaise(b.pending) > 0).reduce((s, b) => s + toPaise(b.pending), 0)) }, who);
        const old = account.bills.filter(b => toPaise(b.pending) > 0 && daysFrom(b.billDate, facts.asOf) >= OLD_BILL_DAYS);
        if (old.length) push('old_open_bill', account.sourceKey, { count: old.length, amount: fromPaise(old.reduce((s, b) => s + toPaise(b.pending), 0)), refs: old.slice(0, 10).map(b => b.ref) }, who);
        const dupRefs = [...new Set(account.bills.filter(b => b.duplicateRef).map(b => b.ref.trim()))];
        for (const ref of dupRefs) push('duplicate_bill_reference', `${account.sourceKey}:${ref.toLowerCase()}`, { refs: [ref], count: account.bills.filter(b => b.duplicateRef && b.ref.trim() === ref).length }, who);
        for (const b of account.bills) {
            if (b.dueDate && b.tallyOverdueDays !== undefined && b.tallyOverdueDays >= 0) countedTo.push({ ref: b.ref, date: addDays(b.dueDate, b.tallyOverdueDays) });
            else if (b.dueDate && b.tallyOverdueDays === undefined) notYetDue.push({ ref: b.ref, due: b.dueDate });
            const p = toPaise(b.pending);
            if (p <= 0) continue;
            if (b.dueDate && b.dueDate === b.billDate) { dueOnBillDate++; dueOnBillDateP += p; }
            if (!b.dueDate) { noDue++; noDueP += p; }
            if (b.billDate > facts.asOf) { future++; futureP += p; }
        }
    }
    if (settings.ageing.basis === 'due_date' && dueOnBillDate) push('due_on_bill_date', 'company', { count: dueOnBillDate, amount: fromPaise(dueOnBillDateP) });
    if (noDue) push('no_due_date', 'company', { count: noDue, amount: fromPaise(noDueP) });
    if (future) push('future_dated_bill', 'company', { count: future, amount: fromPaise(futureP) });
    const working = tallyWorkingDate(countedTo, notYetDue);
    if (working.disagree.length) push('overdue_days_differ', 'company', { count: working.disagree.length, refs: working.disagree.slice(0, 10) });
    else if (working.date && working.date !== facts.asOf) push('tally_working_date', 'company', { date: working.date });
    if (position.summary.excluded.accounts) push('excluded_by_settings', 'company', { count: position.summary.excluded.accounts, amount: position.summary.excluded.net });

    if (input.previous) {
        const now = new Set(facts.accounts.map(a => a.sourceKey));
        for (const a of input.previous.accounts) {
            if (!now.has(a.sourceKey) && toPaise(a.closing) !== 0) push('ledger_no_longer_read', a.sourceKey, { amount: a.closing }, { sourceKey: a.sourceKey, name: a.name });
        }
    }

    const nameOf = (sourceKey: string) => facts.accounts.find(a => a.sourceKey === sourceKey)?.name ?? '';
    const r = input.reconcile;
    if (r) {
        for (const e of r.credits) {
            const who = { sourceKey: e.sourceKey, name: nameOf(e.sourceKey) };
            // A change no voucher explains is its own check (movement_not_explained): not a receipt whose bills are unclear.
            if (e.status === 'AMBIGUOUS' && e.explanation !== 'ambiguous_no_voucher') push('allocation_unclear', `${e.sourceKey}:${e.id}`, { amount: e.amount, date: e.effectiveDate, detail: EXPLANATIONS[e.explanation] }, who);
            if (e.status === 'EXACT_FROM_TALLY' && e.unallocated.length) push('receipt_partly_allocated', `${e.sourceKey}:${e.id}`, { amount: e.amount, date: e.effectiveDate, refs: e.allocations.map(l => l.ref) }, who);
            if ((e.status === 'EXACT_FROM_TALLY' || e.status === 'DERIVED_WITH_HIGH_CONFIDENCE') && e.allocations.length > 1) push('receipt_across_bills', `${e.sourceKey}:${e.id}`, { amount: e.amount, count: e.allocations.length, refs: e.allocations.map(l => l.ref), date: e.effectiveDate }, who);
        }
        for (const v of r.reversals) push('bill_pending_again', `${v.sourceKey}:${v.id}`, { amount: v.amount, refs: v.bills.map(b => b.ref), date: v.effectiveDate }, { sourceKey: v.sourceKey, name: nameOf(v.sourceKey) });
        for (const c of r.changes) push('voucher_changed', `${c.sourceKey}:${c.id}`, { detail: c.change, date: c.before.date, ...(c.before.number ? { refs: [c.before.number] } : {}) }, { sourceKey: c.sourceKey, name: nameOf(c.sourceKey) });
        for (const u of r.unexplained) {
            if (toPaise(u.observedSettled) !== toPaise(u.explainedByVouchers)) push('movement_not_explained', u.sourceKey, { amount: fromPaise(toPaise(u.observedSettled) - toPaise(u.explainedByVouchers)) }, { sourceKey: u.sourceKey, name: nameOf(u.sourceKey) });
        }
    }

    const ch = input.cheques;
    if (ch) {
        const matchOf = new Map(ch.matches.map(m => [m.chequeId, m]));
        for (const c of ch.cheques) {
            const m = matchOf.get(c.id);
            const who = { sourceKey: c.sourceKey, name: nameOf(c.sourceKey) };
            if (m?.state === 'NEEDS_REVIEW') push('cheque_bounced_receipt_stands', `cheque:${c.id}`, { amount: c.amount, refs: [c.chequeNumber] }, who);
            else if (c.status === 'Cleared' && (!m || m.state === 'NOT_YET_SEEN' || m.state === 'POSSIBLE_MATCH') && daysFrom(c.chequeDate, facts.asOf) >= CHEQUE_NOT_IN_TALLY_DAYS) {
                push('cheque_not_in_tally', `cheque:${c.id}`, { amount: c.amount, refs: [c.chequeNumber], date: c.chequeDate }, who);
            }
        }
        if (ch.recordsCheques && r) {
            const linked = new Set(ch.matches.filter(m => m.eventId).map(m => m.eventId!));
            for (const e of r.credits) {
                if (e.voucher?.instrument?.number && !linked.has(e.id)) push('receipt_without_cheque', `${e.sourceKey}:${e.id}`, { amount: e.amount, refs: [e.voucher.instrument.number], date: e.effectiveDate }, { sourceKey: e.sourceKey, name: nameOf(e.sourceKey) });
            }
        }
    }

    for (const a of input.operational ?? []) {
        if (a.state === 'suspended') push('timely_allocation_needs_review', `op:${a.id}`, { amount: fromPaise(a.lines.reduce((s, l) => s + toPaise(l.amount), 0)), refs: a.lines.map(l => l.ref), ...(a.stateReason ? { detail: a.stateReason } : {}) }, { sourceKey: a.sourceKey, name: nameOf(a.sourceKey) });
    }

    return out.sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || a.code.localeCompare(b.code) || (a.account ?? '').localeCompare(b.account ?? ''));
}

export interface FindingsSummary {
    counts: Record<CheckSeverity, number>;
    byCheck: { code: CheckCode; severity: CheckSeverity; title: string; findings: number; amount: number }[];
    /** True when anything blocks connecting. */
    blocked: boolean;
}

export function summarizeFindings(findings: readonly Finding[]): FindingsSummary {
    const counts: Record<CheckSeverity, number> = { information: 0, review: 0, attention: 0, blocks: 0 };
    const by = new Map<CheckCode, { code: CheckCode; severity: CheckSeverity; title: string; findings: number; amountP: number }>();
    for (const f of findings) {
        counts[f.severity]++;
        const e = by.get(f.code) ?? { code: f.code, severity: f.severity, title: checkDefinition(f.code).title, findings: 0, amountP: 0 };
        by.set(f.code, { ...e, findings: e.findings + 1, amountP: e.amountP + toPaise(Math.abs(f.facts.amount ?? 0)) });
    }
    return {
        counts,
        byCheck: [...by.values()]
            .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || b.findings - a.findings)
            .map(({ amountP, ...rest }) => ({ ...rest, amount: fromPaise(amountP) })),
        blocked: counts.blocks > 0,
    };
}

export interface FindingText { title: string; severity: string; found: string; means: string; handling: string; action: string; sync: string }

const n = (count: number | undefined, word: string) => `${count ?? 0} ${word}${count === 1 ? '' : 's'}`;

/** A finding in words, in the company's own money and date formats. */
export function describeFinding(f: Finding, money: (amount: number) => string, day: (iso: string) => string = d => d): FindingText {
    const def = checkDefinition(f.code);
    const who = f.account ? `${f.account}: ` : '';
    const amt = f.facts.amount !== undefined ? money(Math.abs(f.facts.amount)) : '';
    const refs = f.facts.refs?.length ? ` (${f.facts.refs.slice(0, 5).join(', ')}${f.facts.refs.length > 5 ? ', …' : ''})` : '';
    const on = f.facts.date ? ` on ${day(f.facts.date)}` : '';
    let found: string;
    switch (f.code) {
        case 'no_customer_ledgers': found = 'TallyPrime returned no ledgers under Sundry Debtors.'; break;
        case 'company_changed': found = `This read is of “${f.facts.detail ?? ''}”, not the company connected.`; break;
        case 'bills_not_matched': found = `${n(f.facts.count, 'bill')}, ${amt}, could not be tied to one customer${refs}.`; break;
        case 'duplicate_bill_reference': found = `${who}reference ${f.facts.refs?.[0] ?? ''} is on ${n(f.facts.count, 'open bill')}.`; break;
        case 'overdue_days_differ': found = `${n(f.facts.count, 'bill')}${refs}: TallyPrime’s overdue days point to a different date from the other bills’.`; break;
        case 'tally_working_date': found = `TallyPrime is set to ${f.facts.date ? day(f.facts.date) : 'another date'}, not today: its overdue days count to that date.`; break;
        case 'nothing_aged': found = `None of the ${amt} owed is kept bill by bill.`; break;
        case 'bill_pending_again': found = `${who}${amt} pending again${refs}${on}.`; break;
        case 'cheque_bounced_receipt_stands': found = `${who}cheque ${f.facts.refs?.[0] ?? ''} for ${amt} is marked bounced; TallyPrime still records its receipt.`; break;
        case 'on_account_credit': found = `${who}${amt} is in TallyPrime against no bill (On Account).`; break;
        case 'not_bill_wise': found = `${who}${amt}${(f.facts.amount ?? 0) < 0 ? ' in credit' : ''}, kept without bills in TallyPrime.`; break;
        case 'on_account_debit': found = `${who}${amt} is owed with no bill in TallyPrime.`; break;
        case 'old_open_bill': found = `${who}${n(f.facts.count, 'bill')} over a year old, ${amt}${refs}.`; break;
        case 'balance_nil_bills_open': found = `${who}balance is nil, but ${n(f.facts.count, 'bill')} are open (${amt} owed against credit).`; break;
        case 'allocation_unclear': found = `${who}${amt}${on}. ${f.facts.detail ?? ''}`.trim(); break;
        case 'movement_not_explained': found = `${who}bills fell by ${amt} more than the vouchers read explain.`; break;
        case 'ledger_no_longer_read': found = `${who}no longer among the customer ledgers TallyPrime returns (last balance ${amt}).`; break;
        case 'cheque_not_in_tally': found = `${who}cheque ${f.facts.refs?.[0] ?? ''} for ${amt}${on} is cleared in Timely; no receipt in TallyPrime yet.`; break;
        case 'timely_allocation_needs_review': found = `${who}a Timely allocation of ${amt}${refs} no longer fits TallyPrime.`; break;
        case 'credit_reference_open': found = `${who}${amt} held as credit${refs}.`; break;
        case 'account_in_credit': found = `${who}in credit by ${amt}.`; break;
        case 'due_on_bill_date': found = `${n(f.facts.count, 'bill')}, ${amt}, are due on their bill date (no credit period).`; break;
        case 'no_due_date': found = `${n(f.facts.count, 'bill')}, ${amt}, have no due date from TallyPrime.`; break;
        case 'future_dated_bill': found = `${n(f.facts.count, 'bill')}, ${amt}, are dated in the future.`; break;
        case 'excluded_by_settings': found = `${n(f.facts.count, 'account')}, ${amt} net, left out by your settings.`; break;
        case 'receipt_partly_allocated': found = `${who}a receipt of ${amt}${on} is partly against bills${refs}.`; break;
        case 'receipt_across_bills': found = `${who}a receipt of ${amt}${on} is against ${n(f.facts.count, 'bill')}${refs}.`; break;
        case 'voucher_changed': found = `${who}a voucher${refs} dated ${f.facts.date ? day(f.facts.date) : ''} was changed in TallyPrime (${(f.facts.detail ?? '').replace(/_/g, ' ')}).`; break;
        case 'receipt_without_cheque': found = `${who}a receipt of ${amt}${on} carries cheque ${f.facts.refs?.[0] ?? ''}; no cheque in Timely is linked to it.`; break;
    }
    return { title: def.title, severity: SEVERITY_WORDS[f.severity], found, means: def.means, handling: def.handling, action: def.action, sync: def.sync };
}

/** The receipts and other events in one sentence each, for a "Payments and allocations" list. */
export const eventSentence = (e: CreditEvent): string => EXPLANATIONS[e.explanation];
